import { useEffect, useMemo, useRef, useState } from 'react';
import { CAST_POSES, castKey, castSrc, hasPose } from '../data/cast';
import { useInView, useReducedMotion } from './hooks';
import { MOTION_TIMING } from './timing';

// A manager's caricature as a transparent cut-out that can change pose.
// trigger: 'hover' (sequence on hover, return to base), 'inview' (play once when scrolled into view),
//          'cycle' (loop through poses forever), 'none' (controlled via `pose`).
// Poses are hard cuts on purpose: it's a drawn character, not a puppet.
const DEFAULT_SEQUENCE = ['shock', 'point', 'celebrate', 'smug'];

export default function CastFigure({
  name,
  pose,
  sequence = DEFAULT_SEQUENCE,
  trigger = 'hover',
  step = MOTION_TIMING.castPoseStepMs,
  hold = MOTION_TIMING.castPoseHoldMs,
  className = '',
  imgClassName = '',
  alt,
  style,
  fallback = null,
  preload = true,
}) {
  const key = castKey(name);
  const reduced = useReducedMotion();
  const seq = useMemo(() => sequence.filter((p) => hasPose(key, p)), [key, sequence]);
  const [auto, setAuto] = useState('base');
  const [ref, inView] = useInView({ threshold: 0.35, once: trigger !== 'cycle' });
  const timer = useRef(0);
  const hovering = useRef(false);

  useEffect(() => {
    // preload the poses this figure can show so cuts are instant
    if (!key || !preload) return;
    const list = trigger === 'none' ? (CAST_POSES[key] || ['base']) : ['base', ...seq];
    list.forEach((p) => { const img = new Image(); img.src = castSrc(key, p); });
  }, [key, preload, seq, trigger]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const run = (list, onDone) => {
    clearTimeout(timer.current);
    let i = 0;
    const tick = () => {
      if (i >= list.length) { onDone && onDone(); return; }
      setAuto(list[i]); i += 1;
      timer.current = setTimeout(tick, step);
    };
    tick();
  };

  useEffect(() => {
    if (reduced || !key || !seq.length) return undefined;
    if (trigger === 'inview' && inView) {
      run(seq, () => { timer.current = setTimeout(() => setAuto('base'), hold); });
    }
    if (trigger === 'cycle' && inView) {
      let alive = true;
      const loop = () => {
        if (!alive) return;
        run(seq, () => { timer.current = setTimeout(() => { setAuto('base'); timer.current = setTimeout(loop, hold); }, hold); });
      };
      loop();
      return () => { alive = false; clearTimeout(timer.current); };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, inView, reduced, key, seq.join(','), step, hold]);

  const onEnter = () => {
    if (trigger !== 'hover' || reduced || !seq.length) return;
    hovering.current = true;
    run(seq, () => { if (hovering.current) setAuto(seq[seq.length - 1]); });
  };
  const onLeave = () => {
    if (trigger !== 'hover') return;
    hovering.current = false;
    clearTimeout(timer.current);
    setAuto('base');
  };

  if (!key) return fallback;
  const shown = pose && hasPose(key, pose) ? pose : (reduced ? 'base' : auto);

  return (
    <span
      ref={ref}
      className={`cast-figure ${className}`}
      data-pose={shown}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      style={style}
    >
      <img
        className={`cast-figure__img ${imgClassName}`}
        src={castSrc(key, shown)}
        alt={alt ?? `${name} caricature`}
        draggable="false"
        decoding="async"
      />
    </span>
  );
}
